import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Site theme (dark = ink & gold, light = white & gold).
 *
 * Persisted per user in localStorage and applied as `data-theme` on <html>,
 * so the whole palette in index.css flips through CSS variables — no component
 * needs to know about the theme. Dark is the default: users who never open the
 * switcher keep the classic look.
 */
export type ThemeName = 'dark' | 'light'

const STORAGE_KEY = 'app-theme'

/** Browser chrome / Telegram Mini App header colour per theme. */
const CHROME: Record<ThemeName, string> = { dark: '#08080a', light: '#f7f7f8' }

interface ThemeContextValue {
  theme: ThemeName
  setTheme: (theme: ThemeName) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readStored(): ThemeName {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === 'light' || value === 'dark') return value
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return 'dark'
}

function applyTheme(theme: ThemeName) {
  const root = document.documentElement
  root.dataset.theme = theme
  root.style.colorScheme = theme

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', CHROME[theme])

  // Telegram Mini App chrome follows the theme too (it has its own header bar).
  const tg = (window as unknown as { Telegram?: { WebApp?: Record<string, (c: string) => void> } }).Telegram?.WebApp
  try {
    tg?.setHeaderColor?.(CHROME[theme])
    tg?.setBackgroundColor?.(CHROME[theme])
  } catch {
    /* older Bot API versions don't support these calls */
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(readStored)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const persist = useCallback((next: ThemeName) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const setTheme = useCallback(
    (next: ThemeName) => {
      setThemeState(next)
      persist(next)
    },
    [persist],
  )

  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const next: ThemeName = prev === 'dark' ? 'light' : 'dark'
      persist(next)
      return next
    })
  }, [persist])

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
